const DEFAULT_UNDO_LIMIT = 50;

/**
 * Undo/redo stack for executed calendar commands.
 *
 * Entries move between stacks only after a successful undo/redo execution
 * (see takeUndo/confirmUndo and takeRedo/confirmRedo).
 */
export class CalendarUndoStack {
   constructor(limit = DEFAULT_UNDO_LIMIT) {
      this._limit = Math.max(1, Number(limit) || DEFAULT_UNDO_LIMIT);
      this._undo = [];
      this._redo = [];
   }

   push(entry) {
      if (!entry || typeof entry !== "object") return;
      this._undo.push(entry);
      if (this._undo.length > this._limit) {
         this._undo.shift();
      }
      this._redo.length = 0;
   }

   canUndo() {
      return this._undo.length > 0;
   }

   canRedo() {
      return this._redo.length > 0;
   }

   /** Remove the top undo entry without placing it on redo yet. */
   takeUndo() {
      return this._undo.pop() || null;
   }

   /** After a successful undo — entry becomes available for redo. */
   confirmUndo(entry) {
      if (!entry || typeof entry !== "object") return;
      this._redo.push(entry);
   }

   /** Undo failed — put the entry back so the user can retry. */
   restoreUndo(entry) {
      if (!entry || typeof entry !== "object") return;
      this._undo.push(entry);
   }

   /** Remove the top redo entry without placing it on undo yet. */
   takeRedo() {
      return this._redo.pop() || null;
   }

   /** After a successful redo — entry returns to the undo stack. */
   confirmRedo(entry) {
      if (!entry || typeof entry !== "object") return;
      this._undo.push(entry);
      if (this._undo.length > this._limit) {
         this._undo.shift();
      }
   }

   /** Redo failed — put the entry back so the user can retry. */
   restoreRedo(entry) {
      if (!entry || typeof entry !== "object") return;
      this._redo.push(entry);
   }

   clear() {
      this._undo.length = 0;
      this._redo.length = 0;
   }
}

export function createCalendarUndoStack(limit) {
   return new CalendarUndoStack(limit);
}
